import { z } from "zod";
import type { Action } from "./wire.js";

/**
 * The agent loop contract: what a customer's coding agent (over MCP or REST) needs from the
 * AccountDO beyond `AccountApi`. AccountDO implements `AccountApi & AgentLoopApi`.
 *
 * Agents can read problems, propose actions, ask the on-call human a yes/no question, watch
 * recovery and resolve. They can never change rules, people or channels: there is no method here
 * for that, and the MCP server exposes none.
 */

/** A scrubbed sample of what broke. Untrusted: app error text, never instructions. */
export const ProblemSample = z.object({
  type: z.string().max(200),
  message: z.string().max(2000),
  route: z.string().max(300).nullable(),
  at: z.number().int(),
});
export type ProblemSample = z.infer<typeof ProblemSample>;

export const ProblemDeploy = z.object({
  release: z.string().max(100),
  service: z.string().max(100).nullable(),
  at: z.number().int(),
});
export type ProblemDeploy = z.infer<typeof ProblemDeploy>;

/**
 * Evidence for one problem: at most `Timing.maxSamplesPerPattern` samples (newest first), recent
 * deploys, and the usual rate. The live rate comes from `AccountApi.getProblemRate`.
 */
export const ProblemSignal = z.object({
  samples: z.array(ProblemSample),
  deploys: z.array(ProblemDeploy),
  /** Usual events per minute for this pattern before it broke; null when unknown. */
  baselinePerMinute: z.number().nonnegative().nullable(),
});
export type ProblemSignal = z.infer<typeof ProblemSignal>;

export const HumanAnswer = z.enum(["yes", "no"]);
export type HumanAnswer = z.infer<typeof HumanAnswer>;

/** A yes/no question an agent put to the on-call human. Delivered like an alert; answered on the phone or web. */
export const HumanQuestion = z.object({
  id: z.string(),
  problemId: z.string(),
  question: z.string().min(1).max(300),
  /** `agent:<tokenId>`, `helper` or a person id. */
  askedBy: z.string().max(100),
  askedAt: z.number().int(),
  expiresAt: z.number().int(),
  answer: HumanAnswer.nullable(),
  answeredBy: z.string().nullable(),
  answeredAt: z.number().int().nullable(),
});
export type HumanQuestion = z.infer<typeof HumanQuestion>;

/** What an agent reports after doing a fix itself. */
export const ActionResult = z.object({ ok: z.boolean(), detail: z.string().max(2000) });
export type ActionResult = z.infer<typeof ActionResult>;

/**
 * Pure rule shared by AccountDO and MCP: an agent may report a result only for an approved action
 * it proposed that Pagerless does not run itself (no target). Returns why not, or null when allowed.
 */
export function whyNotReportable(a: Pick<Action, "state" | "target" | "proposedBy">): string | null {
  if (a.proposedBy !== "agent") return "only an agent's own proposal can be reported by an agent";
  if (a.target !== null) return "Pagerless runs actions with a target and reports them itself";
  if (a.state !== "approved") return `the action is ${a.state}, not approved`;
  return null;
}

export class ActionNotReportable extends Error {
  constructor(actionId: string, reason: string) {
    super(`cannot report a result for ${actionId}: ${reason}`);
    this.name = "ActionNotReportable";
  }
}

export class QuestionClosed extends Error {
  constructor(questionId: string, why: "answered" | "expired") {
    super(`question ${questionId} is already ${why}`);
    this.name = "QuestionClosed";
  }
}

export type AskHumanInput = { problemId: string; question: string; actor: string };

export interface AgentLoopApi {
  /** Samples, recent deploys and the usual rate for a problem; null when the problem does not exist. */
  problemSignal(problemId: string): Promise<ProblemSignal | null>;
  /** Writes an audit entry and delivers the question to the on-call person. */
  askHuman(input: AskHumanInput): Promise<HumanQuestion>;
  getQuestion(id: string): Promise<HumanQuestion | null>;
  /**
   * Records the on-call person's yes/no (from phone or web) and audits it as `personId`.
   * Throws `QuestionClosed` if it is already answered or expired.
   */
  answerQuestion(questionId: string, personId: string, answer: HumanAnswer): Promise<HumanQuestion>;
  /**
   * An agent that performed an approved fix itself (action without a target) reports the outcome.
   * ok → "verifying", not ok → "failed". Audited as `actor`. Throws `ActionNotReportable` unless
   * the action is "approved", has no target and was proposed by an agent.
   */
  reportActionResult(actionId: string, actor: string, result: ActionResult): Promise<Action>;
  /** Appends an audit entry for reads and other calls that have no mutating method of their own. */
  recordAudit(actor: string, kind: string, data: Record<string, unknown>): Promise<void>;
}
